import { Button } from '@/components/ui/button'
import {
  canPersistErrorReload,
  clearErrorReload,
  isRecentErrorReload,
  markErrorReload,
} from '@/utils/error-refresh.utils'
import { AlertCircle, Home, RefreshCw } from 'lucide-react'
import { useEffect } from 'react'

const isDevelopment = import.meta.env.VITE_ENVIRONMENT === 'DEVELOPMENT'

/**
 * ErrorPage Component
 *
 * - **Development**: shows the error message + component stack so it can be
 *   debugged. No automatic recovery.
 * - **Production**: renders nothing and silently reloads the page ONCE. If the
 *   error comes back right after that reload, the reload clearly didn't help, so
 *   it redirects to `/` instead of reloading again (never an infinite loop).
 */
const ErrorPage = ({ errorMsg, errorInfo }: { errorMsg: Error; errorInfo: React.ErrorInfo }) => {
  const refreshPage = () => window.location.reload()

  useEffect(() => {
    if (isDevelopment) return

    const onHome = window.location.pathname === '/'

    // Storage blocked → we can't tell a first error from a repeat, so never
    // auto-reload (that is what would loop). Redirect Home once instead.
    if (!canPersistErrorReload()) {
      if (!onHome) window.location.replace('/')
      return
    }

    // The error is still here right after a reload → reloading won't fix it.
    if (isRecentErrorReload()) {
      clearErrorReload()
      if (!onHome) window.location.replace('/')
      return
    }

    // First occurrence → one silent reload in case it was transient.
    markErrorReload()
    window.location.reload()
  }, [])

  // Production: never show the error UI. Blank while reloading/redirecting,
  // except the dead-end where `/` itself keeps crashing - show a minimal notice
  // there instead of an infinite loop or a blank screen.
  if (!isDevelopment) {
    const onHome = window.location.pathname === '/'
    const stuckOnHome = onHome && (!canPersistErrorReload() || isRecentErrorReload())
    if (!stuckOnHome) return null

    return (
      <div className='flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900 p-4'>
        <div className='max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center'>
          <div className='flex justify-center mb-6'>
            <div className='bg-red-100 dark:bg-red-900/20 p-3 rounded-full'>
              <AlertCircle className='h-8 w-8 text-red-600 dark:text-red-400' />
            </div>
          </div>
          <h1 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>Something went wrong</h1>
          <p className='text-gray-600 dark:text-gray-400 mb-6'>
            We couldn't recover automatically. Please try again in a moment.
          </p>
          <Button onClick={refreshPage} className='flex items-center gap-2 mx-auto'>
            <RefreshCw className='h-4 w-4' />
            Try Again
          </Button>
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-4'>
            If the problem persists, please contact support
          </p>
        </div>
      </div>
    )
  }

  // Development: full diagnostic page.
  return (
    <div className='flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900'>
      <div className='max-w-2xl w-full mx-4'>
        <div className='bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center'>
          {/* Error Icon */}
          <div className='flex justify-center mb-6'>
            <div className='bg-red-100 dark:bg-red-900/20 p-3 rounded-full'>
              <AlertCircle className='h-8 w-8 text-red-600 dark:text-red-400' />
            </div>
          </div>

          {/* Main Error Title */}
          <h1 className='text-2xl font-bold text-gray-900 dark:text-white mb-2'>Internal Server Error</h1>

          {/* Subtitle */}
          <div className='mb-6'>
            <h2 className='text-3xl font-black text-gray-700 dark:text-gray-300 mb-2'>Oops!</h2>
            <p className='text-gray-600 dark:text-gray-400'>Something went wrong</p>
          </div>

          {/* Error Details */}
          <div className='mb-6 text-left'>
            <div className='bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4'>
              <h3 className='text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2'>Error Message:</h3>
              <p className='text-sm text-red-600 dark:text-red-400 font-mono break-words'>{errorMsg.message}</p>
            </div>

            <div className='bg-gray-100 dark:bg-gray-700 rounded-lg p-4'>
              <h3 className='text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2'>Component Stack:</h3>
              <pre className='text-xs text-gray-600 dark:text-gray-400 font-mono whitespace-pre-wrap break-words max-h-32 overflow-y-auto'>
                {errorInfo?.componentStack}
              </pre>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex flex-col sm:flex-row gap-3 justify-center'>
            <Button onClick={refreshPage} variant='outline' className='flex items-center gap-2'>
              <RefreshCw className='h-4 w-4' />
              Refresh Page
            </Button>
            <Button asChild>
              <a href='/'>
                <Home className='h-4 w-4 mr-2' />
                Go to Home
              </a>
            </Button>
          </div>

          {/* Help Text */}
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-4'>
            If the problem persists, please contact support
          </p>
        </div>
      </div>
    </div>
  )
}

export default ErrorPage
